import { useEffect, useState } from "react";

/**
 * HSTClock — live Hawaii Standard Time clock (UTC−10, no DST).
 *
 * Renders as a compact inline element so it can be dropped into the
 * sidebar, top bar, or dashboard hero. Ticks once per second and cleans
 * up its interval on unmount.
 *
 * Props:
 *   variant:  "sidebar" (default, small stacked) | "hero" (large horizontal)
 *   testId:   optional data-testid override
 */
export default function HSTClock({ variant = "sidebar", testId }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const parts = hstParts(now);
  const testid = testId || `hst-clock-${variant}`;

  if (variant === "hero") {
    return (
      <div data-testid={testid}
           className="inline-flex items-baseline gap-3 whitespace-nowrap">
        <span className="font-display text-[28px] leading-none"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
          {parts.time}
        </span>
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#F2DC8C]/65">
          HST · {parts.date}
        </span>
      </div>
    );
  }

  // sidebar variant — compact stack that fits the marble-and-gold rail.
  return (
    <div data-testid={testid} className="flex flex-col leading-none">
      <span className="font-display text-[15px]"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 55%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
        {parts.time}
      </span>
      <span className="mt-1 text-[8px] uppercase tracking-[0.28em] text-[#F2DC8C]/60">
        HST · {parts.dayShort}
      </span>
    </div>
  );
}

/**
 * hstParts — split `date` into readable HST fields. The Hawaii timezone
 * has no DST so a fixed −10h offset is correct year-round. Using
 * `toLocaleString` with the Pacific/Honolulu IANA zone keeps this
 * accurate even if the runtime happens to be in a different timezone.
 */
function hstParts(date) {
  const time = date.toLocaleTimeString("en-US", {
    timeZone: "Pacific/Honolulu",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dayShort = date.toLocaleDateString("en-US", {
    timeZone: "Pacific/Honolulu",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const dateFull = date.toLocaleDateString("en-US", {
    timeZone: "Pacific/Honolulu",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return { time, dayShort, date: dateFull };
}
